import { Children, ReactNode, useLayoutEffect, useRef } from 'react'
import { CLASSES, FALLBACK_ANIMATION_END, TransitionProps } from './common'
import { buildClassName } from '../../utils/buildClassName'
import { usePrevious, useForceUpdate, useSyncEffect } from '../../utils/hooks'
import { waitForAnimationEnd } from '../../utils/cssAnimationEndListeners'

export function AnimatedSwitch({
  id,
  ref,
  name,
  activeKey,
  direction = 'auto',
  keepDisplayMap,
  slideClassName,
  className,
  styles,
  slideStyles,
  children,
}: TransitionProps) {
  const currentKeyRef = useRef<any>()
  const rendersRef = useRef<Record<number, ReactNode>>({})

  useSyncEffect(() => {
    Children.forEach(children, (child, index) => {
      rendersRef.current[index] = child
    })
  }, [children])

  let containerRef = useRef<HTMLDivElement>()
  if (ref) containerRef = ref

  const prevActiveKey = usePrevious<any>(activeKey)
  const activeKeyChanged =
    prevActiveKey !== undefined && activeKey !== prevActiveKey

  const forceUpdate = useForceUpdate()
  const isBackwards =
    direction === -1 ||
    (direction === 'auto' && prevActiveKey > activeKey) ||
    (direction === 'inverse' && prevActiveKey < activeKey)

  useLayoutEffect(() => {
    const isSlideOptimized =
      name === 'slideOptimized' || name === 'slideOptimizedRtl'

    Children.forEach(children, (child, index) => {
      rendersRef.current[index] = child
    })

    const container = containerRef.current!
    const prevActiveIndex = prevActiveKey
    const activeIndex = activeKey

    const childNodes = Array.from(container.childNodes)
    if (!childNodes.length) return

    const childElements = Array.from(container.children) as HTMLElement[]

    childElements.forEach(el => {
      el.classList.add(CLASSES.slide)
      if (slideClassName) el.classList.add(slideClassName)
      if (slideStyles) Object.assign(el.style, slideStyles)
    })

    if (!activeKeyChanged) {
      if (prevActiveKey === undefined) {
        childNodes.forEach((node, i) => {
          if (node instanceof HTMLElement) {
            node.classList.remove(CLASSES.from)
            node.classList.remove(CLASSES.to)
            node.classList.toggle(CLASSES.active, i === activeIndex)
            node.classList.toggle(
              keepDisplayMap?.[i] ? CLASSES.invisible : CLASSES.inactive,
              i !== activeIndex,
            )
          }
        })
      }

      if (childElements.length === 1) {
        const firstChild = childNodes[activeIndex] as HTMLElement
        firstChild.classList.add(CLASSES.active)

        if (isSlideOptimized) {
          firstChild.style.setProperty('transition', 'none')
          firstChild.style.setProperty('transform', 'translate3d(0, 0, 0)')
        }
      }

      return
    }

    currentKeyRef.current = activeKey

    if (isSlideOptimized) {
      if (!childNodes[activeIndex]) return

      // TODO: Perform optimized Slide
      // performSlideOptimized(
      //   shouldDisableAnimation,
      //   name,
      //   isBackwards,
      //   cleanup,
      //   activeKey,
      //   currentKeyRef,
      //   container,
      //   childNodes[activeIndex],
      //   childNodes[prevActiveIndex],
      //   shouldRestoreHeight,
      //   onStart,
      //   onStop,
      // )

      return
    }

    if (name === 'none') {
      childNodes.forEach((node, i) => {
        if (node instanceof HTMLElement) {
          node.classList.remove(CLASSES.from)
          node.classList.remove(CLASSES.to)
          node.classList.toggle(CLASSES.active, i === activeIndex)
          node.classList.toggle(
            keepDisplayMap?.[i] ? CLASSES.invisible : CLASSES.inactive,
            i !== activeIndex,
          )
        }
      })

      // TODO: cleanup
      return
    }

    childNodes.forEach((node, i) => {
      if (node instanceof HTMLElement) {
        node.classList.remove(CLASSES.active)
        node.classList.toggle(CLASSES.from, i === prevActiveIndex)
        node.classList.toggle(CLASSES.to, i === activeIndex)
        node.classList.toggle(
          keepDisplayMap?.[i] ? CLASSES.invisible : CLASSES.inactive,
          i !== prevActiveIndex && i !== activeIndex,
        )
      }
    })

    container.classList.toggle(`Transition-${name}`, !isBackwards)
    container.classList.toggle(`Transition-${name}Backwards`, isBackwards)

    function onAnimationEnd() {
      if (activeKey !== currentKeyRef.current) return

      container.classList.remove(`Transition-${name}`)
      container.classList.remove(`Transition-${name}Backwards`)

      childNodes.forEach((node, i) => {
        if (node instanceof HTMLElement) {
          node.classList.remove(CLASSES.from)
          node.classList.remove(CLASSES.to)
          node.classList.toggle(CLASSES.active, i === activeIndex)
          node.classList.toggle(
            keepDisplayMap?.[i] ? CLASSES.invisible : CLASSES.inactive,
            i !== activeIndex,
          )
        }
      })

      // TODO: cleanup()
    }

    const watchedNode =
      name === 'reveal' && isBackwards
        ? childNodes[prevActiveIndex]
        : childNodes[activeIndex]

    if (watchedNode)
      waitForAnimationEnd(
        watchedNode,
        onAnimationEnd,
        undefined,
        FALLBACK_ANIMATION_END,
      )
    else onAnimationEnd()
  }, [
    name,
    activeKey,
    prevActiveKey,
    slideClassName,
    slideStyles,
    activeKeyChanged,
    isBackwards,
    forceUpdate,
  ])

  const renders = rendersRef.current
  const renderKeys = Object.keys(renders).map(Number)
  const contents = renderKeys.map(key => {
    const render = renders[key]
    return !render ? undefined : render
  })

  return (
    <div
      ref={containerRef}
      id={id}
      className={buildClassName('Transition', className)}
      style={styles}
    >
      {contents}
    </div>
  )
}
